import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { sendChatMessage } from "../lib/api";
import { useT } from "../lib/i18n";

const STORAGE_KEY = "cc_chat_session";

export default function ChatWidget() {
    const { t, lang } = useT();
    const [open, setOpen] = useState(false);
    const [sessionId, setSessionId] = useState(() => {
        try {
            return localStorage.getItem(STORAGE_KEY) || null;
        } catch {
            return null;
        }
    });
    const [messages, setMessages] = useState([{ id: "greeting", role: "assistant", content: t.chat.greeting }]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const scrollRef = useRef(null);

    // Reset greeting when language changes (only if conversation is just the greeting)
    useEffect(() => {
        setMessages((m) => (m.length <= 1 ? [{ id: "greeting", role: "assistant", content: t.chat.greeting }] : m));
    }, [lang, t.chat.greeting]);

    useEffect(() => {
        if (sessionId) {
            try {
                localStorage.setItem(STORAGE_KEY, sessionId);
            } catch {}
        }
    }, [sessionId]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, open, busy]);

    async function send(text) {
        const content = (text ?? input).trim();
        if (!content || busy) return;
        setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content }]);
        setInput("");
        setBusy(true);
        try {
            const data = await sendChatMessage({ session_id: sessionId, message: content, language: lang });
            if (!sessionId) setSessionId(data.session_id);
            setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: data.reply }]);
        } catch {
            setMessages((m) => [...m, { id: `e-${Date.now()}`, role: "assistant", content: t.chat.error }]);
        } finally {
            setBusy(false);
        }
    }

    function handleKey(e) {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    }

    return (
        <>
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    data-testid="chat-launcher"
                    className="fixed bottom-6 right-6 z-50 group flex items-center gap-3 pl-4 pr-5 h-14 bg-[#1C1C1C] text-[#F9F9F7] shadow-[0_8px_30px_rgba(0,0,0,0.18)] hover:bg-[#9B7A58] transition-colors duration-300"
                >
                    <span className="relative flex items-center justify-center w-7 h-7">
                        <span className="absolute inset-0 rounded-full bg-[#9B7A58]/30 group-hover:bg-[#F9F9F7]/20 animate-ping" />
                        <MessageSquare size={18} strokeWidth={1.5} />
                    </span>
                    <span className="text-[12px] uppercase tracking-[0.22em] font-bold">
                        {t.chat.badge}
                    </span>
                </button>
            )}

            {open && (
                <div
                    className="fixed z-50 inset-x-4 bottom-4 sm:inset-auto sm:right-6 sm:bottom-6 sm:w-[420px] sm:max-w-[calc(100vw-3rem)] h-[min(640px,calc(100vh-3rem))] flex flex-col backdrop-blur-2xl bg-[#F9F9F7]/92 border hair shadow-[0_24px_60px_rgba(0,0,0,0.18)] animate-fade-up"
                    data-testid="chat-panel"
                    role="dialog"
                    aria-label={t.chat.badge}
                >
                    <div className="flex items-center justify-between px-5 py-4 border-b hair">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-[#1C1C1C] text-[#F9F9F7] flex items-center justify-center font-display font-bold text-[12px]">
                                CC
                            </div>
                            <div className="leading-tight">
                                <p className="font-display font-medium text-[15px]">CC Advisor</p>
                                <p className="text-[10px] uppercase tracking-[0.22em] text-[#9B7A58] font-bold">
                                    {t.chat.subline}
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="p-2 -mr-2 text-[#1C1C1C]/70 hover:text-[#1C1C1C]"
                            data-testid="chat-close"
                            aria-label={t.common.close}
                        >
                            <X size={18} strokeWidth={1.5} />
                        </button>
                    </div>

                    <div ref={scrollRef} className="flex-1 overflow-y-auto thin-scroll px-5 py-5 space-y-4" data-testid="chat-messages">
                        {messages.map((m) => (
                            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div
                                    className={`max-w-[85%] px-4 py-3 text-[14px] leading-relaxed ${
                                        m.role === "user" ? "bg-[#1C1C1C] text-[#F9F9F7]" : "bg-white border hair text-[#1C1C1C]"
                                    }`}
                                    data-testid={`chat-msg-${m.role}`}
                                >
                                    {m.content.split("\n").map((line, idx) => (
                                        <p key={`${m.id}-l${idx}`} className={idx > 0 ? "mt-2" : ""}>{line}</p>
                                    ))}
                                </div>
                            </div>
                        ))}
                        {busy && (
                            <div className="flex justify-start">
                                <div className="flex items-center gap-2 px-4 py-3 bg-white border hair text-[#1C1C1C]/60 text-[13px]">
                                    <Loader2 size={14} className="animate-spin" />
                                    {t.chat.thinking}
                                </div>
                            </div>
                        )}
                    </div>

                    {messages.length <= 1 && !busy && (
                        <div className="px-5 pb-3 flex flex-wrap gap-2" data-testid="chat-suggestions">
                            {t.chat.suggestions.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => send(s)}
                                    className="px-3 py-2 text-[11px] border hair hover:border-[#1C1C1C] hover:text-[#1C1C1C] text-[#1C1C1C]/70 transition-colors"
                                    data-testid="chat-suggestion-btn"
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="border-t hair p-3 flex items-end gap-2">
                        <textarea
                            rows={1}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKey}
                            placeholder={t.chat.placeholder}
                            className="flex-1 resize-none bg-transparent px-3 py-2 text-[14px] outline-none border-b border-transparent focus:border-[#1C1C1C] transition-colors"
                            data-testid="chat-input"
                            disabled={busy}
                        />
                        <button
                            type="button"
                            onClick={() => send()}
                            disabled={busy || !input.trim()}
                            className="h-10 w-10 flex items-center justify-center bg-[#1C1C1C] text-[#F9F9F7] disabled:opacity-40 hover:bg-[#9B7A58] transition-colors"
                            data-testid="chat-send"
                            aria-label="Send"
                        >
                            <Send size={16} strokeWidth={1.5} />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
